import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { Link, useNavigate } from 'react-router-dom';
import logoIcon from '../assets/logo-icon-exact.png';
import SubscribeModal from '../components/SubscribeModal';
import Footer from '../components/Footer';

function ResetPassword({ darkMode, toggleDark }) {
  const navigate = useNavigate();
  const [phase, setPhase]       = useState('loading'); // loading | form | success | invalid
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    // Supabase fires PASSWORD_RECOVERY when the reset hash is processed
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setPhase('form');
    });

    // If Supabase processed the hash before this component mounted, check the session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && window.location.hash.includes('type=recovery')) {
        setPhase('form');
      }
    });

    // After 4 s with no recovery event, the link is invalid or expired
    const timer = setTimeout(() => {
      setPhase(p => p === 'loading' ? 'invalid' : p);
    }, 4000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    setError('');
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setError(error.message);
      setLoading(false);
    } else {
      setPhase('success');
    }
  };

  const header = (
    <header>
      <div className="logo" onClick={() => navigate('/')}>
        <div className="logo-icon"><img src={logoIcon} alt="" /></div>
        <div className="logo-text">TradeReady<span>Pro</span></div>
      </div>
      <div className="header-right">
        <Link to="/subscribe" className="btn-subscribe">Subscribe</Link>
        <Link to="/settings" className="dark-toggle" title="Settings">⚙️</Link>
      </div>
    </header>
  );

  return (
    <div className="auth-page">
      {header}

      <div className="auth-wrap">
        <div className="auth-card">

          {/* Loading — waiting for Supabase to process the hash */}
          {phase === 'loading' && (
            <>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⏳</div>
              <h2>Verifying link…</h2>
              <p className="auth-sub">Please wait while we verify your reset link.</p>
            </>
          )}

          {/* Invalid / expired link */}
          {phase === 'invalid' && (
            <>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
              <h2>Link Expired</h2>
              <p className="auth-sub">
                This password reset link is invalid or has expired. Reset links are valid for one hour.
              </p>
              <Link to="/login" className="btn-auth" style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '0.5rem' }}>
                Request a new link
              </Link>
            </>
          )}

          {/* Reset form */}
          {phase === 'form' && (
            <>
              <h2>Set New Password</h2>
              <p className="auth-sub">Choose a strong password for your account.</p>

              {error && <div className="auth-error">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    minLength={6}
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label>Confirm Password</label>
                  <input
                    type="password"
                    value={confirm}
                    onChange={e => setConfirm(e.target.value)}
                    placeholder="Repeat your new password"
                    minLength={6}
                    required
                  />
                </div>
                <button type="submit" className="btn-auth" disabled={loading}>
                  {loading ? 'Updating…' : 'Update Password'}
                </button>
              </form>
            </>
          )}

          {/* Success */}
          {phase === 'success' && (
            <>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>✅</div>
              <h2>Password Updated</h2>
              <div className="auth-success" style={{ marginTop: '0.75rem' }}>
                Your password has been changed successfully.
              </div>
              <p className="auth-sub" style={{ marginTop: '0.5rem', marginBottom: '1.25rem' }}>
                You can now sign in with your new password.
              </p>
              <Link
                to="/login"
                className="btn-auth"
                style={{ display: 'block', textAlign: 'center', textDecoration: 'none' }}
              >
                Sign In
              </Link>
            </>
          )}

        </div>
      </div>

      {modalOpen && <SubscribeModal onClose={() => setModalOpen(false)} session={null} />}
      <Footer />
    </div>
  );
}

export default ResetPassword;
